import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styles from './FightCards.module.scss';
import { Routes } from '../../../types/constants';
import { fetchFightCardEventDetail } from '../../../services/fight-cards.service';
import { EventDetail, FightCardItem } from '../../../types/fight-cards.types';
import MediaButton from '../../Buttons/MediaButton/MediaButton';
import { ButtonStyleType, MediaButtonType } from '../../../types/button.types';
import FighterCornerBars from '../../FighterCornerBars/FighterCornerBars';
import { formatVideoCardDate, formatVideoCardFightNumber } from '../../../utils/video-card';
import { VideoCardParticipant } from '../../../types/video-card.types';
import { orderVideoCardParticipants } from '../../../utils/video-card';
import { usePurchaseStatus } from '../../../providers/purchase-status.provider';
import { usePurchaseFlow } from '../../../hooks/usePurchaseFlow';
import { useSaveVideo } from '../../../hooks/useSaveVideo';
import SearchDiscoveryPanels from '../../Search/SearchDiscoveryPanels';
import { buildTagResultsRoute, buildTextResultsRoute } from '../../../utils/search-routes';
import { useContinueWatching } from '../../../hooks/useContinueWatching';
import WatchMomentumPanel from '../../WatchMomentum/WatchMomentumPanel';
import { useWatchState } from '../../../providers/watch-state.provider';
import { buildResumeUrlForCard } from '../../../utils/watch-state';

const buildParticipantsFromItem = (item: FightCardItem): VideoCardParticipant[] => {
    if (item.participants && item.participants.length > 0) {
        return item.participants.map((participant) => ({
            name: participant.ParticipantName,
            cornerColor: participant.CornerColor ?? null
        }));
    }

    if (item.type === 'fight') {
        return [
            { name: item.fighterA ?? 'TBD', cornerColor: null },
            { name: item.fighterB ?? 'TBD', cornerColor: null }
        ];
    }

    if (item.guest) {
        return [{ name: item.guest, cornerColor: null }];
    }

    return [{ name: item.title, cornerColor: null }];
};

const pushUniqueName = (values: string[], value?: string | null) => {
    const trimmed = value?.trim();
    if (!trimmed || values.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
        return;
    }

    values.push(trimmed);
};

const FightCardEvent = () => {
    const navigate = useNavigate();
    const { isVideoOwned } = usePurchaseStatus();
    const { startPurchase, isProcessing: isPurchaseProcessing } = usePurchaseFlow();
    const { toggleSaved, isSaved } = useSaveVideo();
    const { getWatchStateByIdentity } = useWatchState();
    const continueWatching = useContinueWatching({ limit: 4 });
    const { eventId } = useParams();
    const [event, setEvent] = useState<EventDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!eventId) {
            setIsLoading(false);
            setEvent(null);
            return;
        }

        let isActive = true;

        const loadEvent = async () => {
            setIsLoading(true);
            setErrorMessage(null);

            try {
                const response = await fetchFightCardEventDetail(eventId, true);
                if (!isActive) {
                    return;
                }
                if (!response?.id) {
                    setEvent(null);
                    return;
                }

                setEvent({
                    ...response,
                    items: response.items ?? []
                });
            } catch (error) {
                if (!isActive) {
                    return;
                }
                setErrorMessage('We ran into an issue loading this event. Please try again soon.');
                setEvent(null);
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        loadEvent();

        return () => {
            isActive = false;
        };
    }, [eventId]);

    const eventDiscoveryTags = useMemo(() => (
        (event?.tags ?? []).map((tag) => ({ id: tag, name: tag }))
    ), [event]);

    const eventFighters = useMemo(() => {
        const fighters: string[] = [];

        event?.items.forEach((item) => {
            if (item.participants && item.participants.length > 0) {
                item.participants.forEach((participant) => {
                    if (participant.ParticipantType.toLowerCase() === 'fighter') {
                        pushUniqueName(fighters, participant.ParticipantName);
                    }
                });
                return;
            }

            if (item.type === 'fight') {
                pushUniqueName(fighters, item.fighterA);
                pushUniqueName(fighters, item.fighterB);
            }
        });

        return fighters;
    }, [event]);

    const eventDiscoveryEvents = event?.eventName ? [event.eventName] : [];
    const hasEventDiscovery = eventDiscoveryTags.length > 0 || eventFighters.length > 0 || eventDiscoveryEvents.length > 0;
    const hasContinueWatching = continueWatching.isLoading || continueWatching.items.length > 0;

    const handlePurchase = async (videoId: string) => {
        await startPurchase(videoId);
    };

    if (isLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.content}>
                    <div className={styles.eventDetailHeader}>
                        <div className={styles.skeletonLineShort} />
                        <div className={styles.skeletonLine} />
                        <div className={styles.skeletonLineSmall} />
                    </div>
                    <div className={styles.eventDetailBody}>
                        <div className={styles.skeletonLine} />
                        <div className={styles.skeletonLineShort} />
                        <div className={styles.skeletonLine} />
                    </div>
                </div>
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className={styles.page}>
                <div className={styles.content}>
                    <div className={styles.stateMessage}>
                        <h1>Unable to load event</h1>
                        <p>{errorMessage}</p>
                        <Link to={Routes.FIGHT_CARDS} className={styles.viewLink}>Back to Fight Cards</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className={styles.page}>
                <div className={styles.content}>
                    <div className={styles.eventNotFound}>
                        <h1>Event not found</h1>
                        <p>We couldn’t find that fight card. Return to the full list to continue browsing.</p>
                        <Link to={Routes.FIGHT_CARDS} className={styles.viewLink}>Back to Fight Cards</Link>
                    </div>
                </div>
            </div>
        );
    }

    const eventDiscoveryPanel = hasEventDiscovery ? (
        <aside className={styles.eventDiscoveryPanel} aria-label="Event discovery">
            <h2 className={styles.eventSideTitle}>Explore this event</h2>
            <SearchDiscoveryPanels
                tags={eventDiscoveryTags}
                fighters={eventFighters}
                events={eventDiscoveryEvents}
                showVideos={false}
                showViewAll={false}
                onTagSelect={(tag) => navigate(buildTagResultsRoute(tag))}
                onFighterSelect={(fighter) => navigate(buildTextResultsRoute(fighter))}
                onEventSelect={(eventName) => navigate(buildTextResultsRoute(eventName))}
            />
        </aside>
    ) : null;
    const eventContinuePanel = hasContinueWatching ? (
        <WatchMomentumPanel
            className={styles.eventContinuePanel}
            variant="sidebar"
            continueItems={continueWatching.items}
            isContinueLoading={continueWatching.isLoading}
            continueTitle="Continue Watching"
            continueSubtitle="Resume another recent fight."
        />
    ) : null;
    const eventSideRail = eventDiscoveryPanel || eventContinuePanel ? (
        <aside className={styles.eventSideRail} aria-label="Event support">
            {eventDiscoveryPanel}
            {eventContinuePanel}
        </aside>
    ) : null;

    return (
        <div className={styles.page}>
            <div className={styles.content}>
                <div className={styles.eventDetailLayout}>
                    <div className={styles.eventDetailMain}>
                        <div className={styles.eventDetailHeader}>
                            <Link to={Routes.FIGHT_CARDS} className={styles.backLink}>← Back to Fight Cards</Link>
                            <h1>{event.eventName}</h1>
                            <p>{[event.venueName, event.location, new Date(event.eventDate).toLocaleDateString()].filter(Boolean).join(' · ')}</p>
                            {event.tags && event.tags.length > 0 ? (
                                <div className={styles.tagRow}>
                                    {event.tags.map((tag) => (
                                        <span key={tag} className={styles.tagChip}>#{tag}</span>
                                    ))}
                                </div>
                            ) : null}
                            <div className={styles.eventLinks}>
                                {event.eventUrl ? (
                                    <a href={event.eventUrl} target="_blank" rel="noreferrer">Event site</a>
                                ) : null}
                                {event.venueUrl ? (
                                    <a href={event.venueUrl} target="_blank" rel="noreferrer">Venue info</a>
                                ) : null}
                            </div>
                        </div>

                        <div className={styles.eventDetailBody}>
                            <h2>Fight Card &amp; Interviews</h2>
                            {event.items.length === 0 ? (
                                <div className={styles.stateMessage}>
                                    <h2>No fight card items yet</h2>
                                    <p>Check back soon for the full lineup and interviews.</p>
                                </div>
                            ) : (
                                <ul className={styles.itemList}>
                                    {[...event.items]
                                        .sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER))
                                        .map((item) => {
                                            const participants = buildParticipantsFromItem(item);
                                            const orderedParticipants = orderVideoCardParticipants(participants);
                                            const leftParticipant = orderedParticipants[0];
                                            const rightParticipant = orderedParticipants[1];
                                            const fightNumberLabel = formatVideoCardFightNumber(item.order ?? null);
                                            const dateLabel = formatVideoCardDate(event.eventDate);
                                            const metaLine = [fightNumberLabel, dateLabel].filter(Boolean).join(' | ');
                                            const isOwned = isVideoOwned(item.id);
                                            const hasValidPurchaseId = /^[0-9a-fA-F-]{36}$/.test(item.id);
                                            const canPurchase = hasValidPurchaseId && !isOwned;
                                            const canSave = hasValidPurchaseId;
                                            const saved = isSaved(item.id);
                                            const watchState = getWatchStateByIdentity(item.id, item.watchUrl);
                                            const itemWatchUrl = item.watchUrl && watchState?.kind === 'in-progress'
                                                ? buildResumeUrlForCard(item.watchUrl, watchState.resumeSeconds)
                                                : item.watchUrl;

                                            const thumbnail = item.thumbnailBase64 ? (
                                                <img
                                                    src={`data:image/png;base64,${item.thumbnailBase64}`}
                                                    alt={item.title}
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className={styles.itemThumbnailPlaceholder} />
                                            );

                                            return (
                                                <li key={item.id} className={`${styles.item} ${styles.itemWithThumbnail}`}>
                                                    <div className={styles.itemThumbnailShell}>
                                                        {item.watchUrl ? (
                                                            <Link
                                                                to={itemWatchUrl ?? item.watchUrl}
                                                                className={styles.itemThumbnailLink}
                                                                aria-label={`${watchState?.kind === 'in-progress' ? 'Resume' : 'Watch'} ${item.title}`}
                                                            >
                                                                {thumbnail}
                                                                <span className={styles.itemThumbnailOverlay}>
                                                                    <span className={styles.itemPlayIcon}>
                                                                        <MediaButton buttonType={MediaButtonType.PLAY} styleType={ButtonStyleType.OUTLINE} />
                                                                    </span>
                                                                </span>
                                                                {watchState ? (
                                                                    <>
                                                                        <span className={`${styles.thumbnailWatchStateBadge} ${watchState.kind === 'watched' ? styles.thumbnailWatchStateBadgeComplete : ''}`}>
                                                                            {watchState.kind === 'in-progress' ? 'Resume' : 'Watched'}
                                                                        </span>
                                                                        <span className={styles.itemProgressTrack} aria-hidden="true">
                                                                            <span
                                                                                className={watchState.kind === 'in-progress' ? styles.itemProgressFill : styles.itemProgressFillComplete}
                                                                                style={{ width: `${watchState.progressPercent}%` }}
                                                                            />
                                                                        </span>
                                                                    </>
                                                                ) : null}
                                                            </Link>
                                                        ) : (
                                                            <div className={styles.itemThumbnail}>
                                                                {thumbnail}
                                                            </div>
                                                        )}
                                                        {canSave ? (
                                                            <button
                                                                type="button"
                                                                className={`${styles.saveButton} ${saved ? styles.saveButtonActive : ''}`}
                                                                aria-label={`${saved ? 'Remove from saved' : 'Save for later'}: ${item.title}`}
                                                                aria-pressed={saved}
                                                                onClick={() => void toggleSaved(item.id)}
                                                            >
                                                                <span className={`material-symbols-outlined ${saved ? styles.saveIconActive : ''}`}>
                                                                    {saved ? 'bookmark' : 'bookmark_add'}
                                                                </span>
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                    <div className={styles.itemContent}>
                                                        <div className={styles.itemHeader}>
                                                            <span className={item.type === 'fight' ? styles.fightTag : styles.interviewTag}>
                                                                {item.type === 'fight' ? 'Fight' : 'Interview'}
                                                            </span>
                                                            <FighterCornerBars
                                                                leftName={leftParticipant?.name ?? 'TBD'}
                                                                leftColor={leftParticipant?.cornerColor ?? null}
                                                                rightName={rightParticipant?.name ?? 'TBD'}
                                                                rightColor={rightParticipant?.cornerColor ?? null}
                                                                layout="row"
                                                                size="sm"
                                                            />
                                                            {isOwned ? (
                                                                <span className={styles.ownedBadge}>Owned</span>
                                                            ) : null}
                                                            {watchState ? (
                                                                <span className={`${styles.watchStateBadge} ${watchState.kind === 'watched' ? styles.watchStateBadgeComplete : ''}`}>
                                                                    {watchState.kind === 'in-progress' ? 'Resume' : 'Watched'}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <h3 className={styles.itemTitle}>{item.title}</h3>
                                                        {metaLine ? <p className={styles.itemMeta}>{metaLine}</p> : null}
                                                        {watchState ? (
                                                            <p className={styles.itemWatchStateMeta}>
                                                                {watchState.kind === 'in-progress'
                                                                    ? `Resume at ${watchState.resumeLabel}`
                                                                    : `Watched ${watchState.lastWatchedLabel}`}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                    <div className={styles.itemActions}>
                                                        {itemWatchUrl ? (
                                                            <Link to={itemWatchUrl} className={styles.watchLink}>
                                                                {watchState?.kind === 'in-progress' ? 'Resume' : 'Watch'}
                                                            </Link>
                                                        ) : null}
                                                        {canPurchase ? (
                                                            <button
                                                                type="button"
                                                                className={styles.buyLink}
                                                                disabled={isPurchaseProcessing}
                                                                onClick={() => handlePurchase(item.id)}
                                                            >
                                                                {isPurchaseProcessing ? 'Starting checkout...' : 'Buy Now'}
                                                            </button>
                                                        ) : null}
                                                        {item.venueUrl ? (
                                                            <a href={item.venueUrl} target="_blank" rel="noreferrer" className={styles.venueLink}>
                                                                Venue
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                </ul>
                            )}
                        </div>
                    </div>
                    {eventSideRail}
                </div>
            </div>
        </div>
    );
};

export default FightCardEvent;
